import type { LocaleKey } from 'garaje/utils/locale-options';
import { TrackedObject } from 'tracked-built-ins';

export const ORIGINAL_OBJECT = Symbol('garaje:translations:original-object');

export type CustomTranslations<T extends string> = {
  [key in T]: {
    [key: LocaleKey]: string;
  };
};

export function createLocalizedProxy<
  T extends { customTranslations: CustomTranslations<TranslatableAttributes> },
  TranslatableAttributes extends string,
>(field: T, translatableAttributes: string[], locale: string): T {
  const proxy = {
    get(target: T, prop: string | symbol, _receiver: unknown): unknown {
      if (prop === ORIGINAL_OBJECT) {
        return target;
      }
      if (translatableAttributes.includes(<string>prop)) {
        return target.customTranslations?.[<TranslatableAttributes>prop]?.[locale];
      }

      return target[<keyof T>prop];
    },

    has(target: T, prop: string | symbol) {
      if (prop === ORIGINAL_OBJECT) {
        return true;
      }
      return Reflect.has(target, prop);
    },

    set(target: T, prop: string | symbol, value: unknown) {
      if (translatableAttributes.includes(<string>prop)) {
        if (!target.customTranslations) {
          const obj = <CustomTranslations<TranslatableAttributes>>(
            Object.fromEntries(translatableAttributes.map((attr) => [attr, new TrackedObject()]))
          );
          target.customTranslations = new TrackedObject(obj);
        }
        target.customTranslations[<TranslatableAttributes>prop] ??= new TrackedObject({});
        // @ts-expect-error
        target.customTranslations[<TranslatableAttributes>prop][locale] = <string>value;
      } else {
        // @ts-expect-error
        target[<keyof T>prop] = value;
      }
      return true;
    },
  };

  return new Proxy(field, proxy);
}
